import { Injectable, Injector } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { catchAndRethrow, ClearNotifications } from '@platform-frontend/util';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Modal } from './modal';
import { ModalCloseResult } from './modal-close-result';
import { ModalContainerController } from './modal-container-controller';
import { ModalContainerComponent } from './modal-container/modal-container.component';
import { ModalData } from './modal-data';
import { ModalReloadDataService } from './modal-reload-data.service';

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  constructor(private injector: Injector, private modalReloadDataService: ModalReloadDataService, private store: Store) {}

  private currentModal = new BehaviorSubject<NgbModalRef | null>(null);
  private showNotifications = new BehaviorSubject<boolean>(true);

  open<R = unknown>(modalIdentifier: symbol, data: ModalData = null, isClosable: boolean = true): Modal<R> {
    const modal = this.getCurrentOrNewModal();
    const container: ModalContainerComponent = modal.componentInstance;

    container.loadComponent(modalIdentifier, data, isClosable);

    from(modal.result)
      .pipe(
        catchAndRethrow(result => this.clearNotificationsAndNullifyCurrentModal((result as ModalCloseResult<any>)?.clearNotifications ?? true)),
        switchMap(result => this.clearNotificationsAndNullifyCurrentModal((result as ModalCloseResult<R>)?.clearNotifications ?? true))
      )
      .subscribe();

    return modal;
  }

  private getCurrentOrNewModal(): NgbModalRef {
    if (this.currentModal.value === null) {
      const controller = new ModalContainerController();
      const modal = this.createModal(controller);
      this.currentModal.next(modal);
      return modal;
    } else {
      return this.currentModal.value;
    }
  }

  private createModal(controller: ModalContainerController): NgbModalRef {
    const ngbModal = this.injector.get(NgbModal);
    const modal = ngbModal.open(ModalContainerComponent, this.buildNgbModalOptions(controller));
    controller.setModalRef(modal);
    return modal;
  }

  private buildNgbModalOptions(controller: ModalContainerController): NgbModalOptions {
    return {
      backdrop: 'static',
      injector: Injector.create({
        providers: [{ provide: ModalContainerController, useValue: controller, deps: [] }]
      }),
      keyboard: false
    };
  }

  private clearNotificationsAndNullifyCurrentModal(clearNotifications: boolean): Observable<unknown> {
    return this.showNotifications.pipe(
      take(1),
      switchMap(showNotifications => (showNotifications && clearNotifications ? this.store.dispatch(new ClearNotifications()) : of(null))),
      tap(() => this.currentModal.next(null)),
      tap(() => this.modalReloadDataService.clearReloadData())
    );
  }

  isOpen(): Observable<boolean> {
    return this.currentModal.pipe(
      map(modal => modal !== null),
      distinctUntilChanged()
    );
  }

  closeCurrent(result?: ModalCloseResult): Observable<unknown> {
    return this.currentModal.pipe(
      take(1),
      tap(modal => modal?.close(result)),
      switchMap(() => this.isOpen()),
      filter(isOpen => !isOpen),
      take(1)
    );
  }

  hasNotifications(): Observable<boolean> {
    return this.showNotifications.pipe(distinctUntilChanged());
  }

  enableNotifications(): void {
    this.showNotifications.next(true);
  }

  disableNotifications(): void {
    this.showNotifications.next(false);
  }
}
